import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
ScrollTrigger.config({ ignoreMobileResize: true });

/* =========================
   CONFIG
========================= */
const SCENE_WIDTH = 11520;
const BASE_HEIGHT = 1000;
const SPOT_OFFSET = 100;

const LOW_HEIGHT_LIMIT = 450;
const LANDSCAPE_MIN_SCALE = 0.55;
const LANDSCAPE_MAX_SCALE = 0.85;

const RIGHT_PADDING_BASE = 220;

/* =========================
   DOM
========================= */
const container = document.querySelector(".horizontal.section-home");
const wrapper = container?.querySelector(".horizontal__inner");
const scaleEl = wrapper?.querySelector(".scene-scale");

const prevBtn = container?.querySelector(".horizontal-arrow--prev");
const nextBtn = container?.querySelector(".horizontal-arrow--next");
const progressBar = document.querySelector(".scroll-progress__bar");

const modal = document.getElementById("hotspotModal");
const modalBody = document.getElementById("hotspotModalContent");

if (container && wrapper && scaleEl) {
    let st = null;
    let drag = null;
    let borderObserver = null;
    let resizeRAF = null;
    let initRAF = null;
    let lastFocusedElement = null;
    let lastInteractionWasKeyboard = false;

    /* =========================
       DEVICE
    ========================= */
    function isTouchDevice() {
        return (
            window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
            window.matchMedia("(any-hover: none) and (any-pointer: coarse)").matches ||
            navigator.maxTouchPoints > 0 ||
            "ontouchstart" in window
        );
    }

    function updateRootTouchClass() {
        document.documentElement.classList.toggle("is-touch-device", isTouchDevice());
    }

    updateRootTouchClass();

    window.addEventListener("keydown", (e) => {
        if (e.key === "Tab" || e.key === "Enter" || e.key === " ") {
            lastInteractionWasKeyboard = true;
        }
    });

    window.addEventListener("mousedown", () => {
        lastInteractionWasKeyboard = false;
    }, true);

    window.addEventListener("pointerdown", () => {
        lastInteractionWasKeyboard = false;
    }, true);

    window.addEventListener("touchstart", () => {
        lastInteractionWasKeyboard = false;
    }, true);

    /* =========================
       HELPERS
    ========================= */
    function getStableVH() {
        const vv = window.visualViewport;
        const h1 = vv ? vv.height : 0;
        const h2 = window.innerHeight || 0;
        const h3 = document.documentElement.clientHeight || 0;
        return Math.max(h1, h2, h3);
    }

    function isLandscapeLowHeight() {
        return getStableVH() < LOW_HEIGHT_LIMIT;
    }

    function getScale() {
        const vh = getStableVH();
        const raw = vh / BASE_HEIGHT;
        const touch = isTouchDevice();

        if (vh < LOW_HEIGHT_LIMIT) {
            return Math.min(LANDSCAPE_MAX_SCALE, Math.max(LANDSCAPE_MIN_SCALE, raw));
        }

        if (touch) {
            return Math.min(1, raw);
        }

        return raw;
    }

    function applyScale() {
        const s = getScale();
        scaleEl.style.transformOrigin = "left top";
        scaleEl.style.transform = `scale(${s})`;
    }

    function getAllSpots() {
        return Array.from(container.querySelectorAll(".spot"));
    }

    function getLastSpotBaseRight() {
        const spots = getAllSpots();
        if (!spots.length) return SCENE_WIDTH;

        let maxRight = 0;

        spots.forEach((spot) => {
            const right = spot.offsetLeft + (spot.offsetWidth || 0);
            if (right > maxRight) maxRight = right;
        });

        return maxRight;
    }

    function getMaxUsefulBaseX() {
        return Math.min(SCENE_WIDTH, getLastSpotBaseRight() + RIGHT_PADDING_BASE);
    }

    function getMaxScrollDistanceScreen() {
        const s = getScale();
        return Math.max(0, (getMaxUsefulBaseX() * s) - window.innerWidth);
    }

    // лишаємо для сумісності
    function getScrollDistanceScreen() {
        return getMaxScrollDistanceScreen();
    }

    function setProgressUI(progress) {
        const clamped = Math.max(0, Math.min(1, progress));

        if (progressBar) {
            gsap.set(progressBar, { scaleX: clamped });
        }

        if (prevBtn) {
            prevBtn.classList.toggle("is-disabled", clamped <= 0.001);
        }

        if (nextBtn) {
            nextBtn.classList.toggle("is-disabled", clamped >= 0.999);
        }
    }

    function applyParallaxByScreenX(screenX) {
        const s = getScale();
        const dist = getMaxScrollDistanceScreen();
        const clampedX = Math.max(0, Math.min(dist, screenX));
        const progress = dist === 0 ? 0 : clampedX / dist;
        const baseX = s === 0 ? 0 : clampedX / s;

        gsap.set(".layer--front", { x: -baseX });
        gsap.set(".hotspots", { x: -baseX });
        gsap.set(".layer--middle", { x: -baseX * 0.9 });
        gsap.set(".layer--back", { x: -baseX * 0.7 });
        gsap.set(".layer--sky", { x: -baseX * 0.5 });

        setProgressUI(progress);
    }

    function scrollSceneToStart(duration = 0.6) {
        if (isTouchDevice() && drag) {
            drag.setX(0, duration > 0);
            return;
        }

        if (!isTouchDevice() && st) {
            gsap.to(window, {
                scrollTo: st.start,
                duration,
                ease: "power2.out",
                overwrite: "auto"
            });
        }
    }

    /* =========================
       MODAL
    ========================= */
    function fitSketchBorder() {
        if (!modal) return;

        const dialog = modal.querySelector(".modal-dialog--sketch");
        const svg = dialog?.querySelector(".sketch-border");
        const outer = svg?.querySelector(".sketch-border__outer");
        const inner = svg?.querySelector(".sketch-border__inner");

        if (!dialog || !svg || !outer || !inner) return;

        const w = dialog.clientWidth;
        const h = dialog.clientHeight;

        svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
        svg.setAttribute("preserveAspectRatio", "none");

        const padOuter = 7;
        const padInner = 13;

        outer.setAttribute("x", padOuter);
        outer.setAttribute("y", padOuter);
        outer.setAttribute("width", Math.max(0, w - padOuter * 2));
        outer.setAttribute("height", Math.max(0, h - padOuter * 2));
        outer.setAttribute("rx", 22);

        inner.setAttribute("x", padInner);
        inner.setAttribute("y", padInner);
        inner.setAttribute("width", Math.max(0, w - padInner * 2));
        inner.setAttribute("height", Math.max(0, h - padInner * 2));
        inner.setAttribute("rx", 18);
    }

    function ensureBorderObserver() {
        if (!modal || borderObserver) return;

        const dialog = modal.querySelector(".modal-dialog--sketch");
        if (!dialog) return;

        borderObserver = new ResizeObserver(() => fitSketchBorder());
        borderObserver.observe(dialog);
    }

    function stopModalMedia() {
        if (!modal) return;

        modal.querySelectorAll("video").forEach((v) => {
            try {
                v.pause();
                v.currentTime = 0;
                v.removeAttribute("autoplay");
            } catch (_) {}
        });

        modal.querySelectorAll("audio").forEach((a) => {
            try {
                a.pause();
                a.currentTime = 0;
            } catch (_) {}
        });

        modal.querySelectorAll("iframe").forEach((frame) => {
            try {
                const src = frame.getAttribute("src");
                if (src) frame.setAttribute("src", src);
            } catch (_) {}
        });
    }

    function getFocusableElementsInModal() {
        if (!modal) return [];
        return Array.from(
            modal.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    }

    function openSpotModal(contentHtml) {
        if (!modal || !modalBody) return;

        lastFocusedElement = document.activeElement;
        modalBody.innerHTML = contentHtml;
        modal.setAttribute("aria-hidden", "false");

        if (window.$) {
            $(modal).modal({
                backdrop: true,
                keyboard: false,
                show: true
            });
        }
    }

    if (modal && window.$) {
        $(modal).on("hide.bs.modal hidden.bs.modal", stopModalMedia);

        $(modal).on("shown.bs.modal", () => {
            requestAnimationFrame(() => {
                fitSketchBorder();
                ensureBorderObserver();

                const focusable = getFocusableElementsInModal();
                if (focusable.length) {
                    focusable[0].focus();
                }
            });
        });

        $(modal).on("hidden.bs.modal", () => {
            stopModalMedia();
            modal.setAttribute("aria-hidden", "true");

            if (lastFocusedElement) {
                lastFocusedElement.focus({ preventScroll: true });
            }
        });

        modal.addEventListener("keydown", (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                $(modal).modal("hide");
                return;
            }

            if (e.key !== "Tab") return;

            const focusable = getFocusableElementsInModal();
            if (!focusable.length) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        });
    }

    /* =========================
       DESTROY
    ========================= */
    function destroyDesktop() {
        if (st) {
            st.kill(true);
            st = null;
        }
    }

    function destroyMobile() {
        if (!drag) return;

        if (drag.usePointer) {
            container.removeEventListener("pointerdown", drag.onDown);
            container.removeEventListener("pointermove", drag.onMove);
            container.removeEventListener("pointerup", drag.onUp);
            container.removeEventListener("pointercancel", drag.onUp);
        } else {
            container.removeEventListener("touchstart", drag.onTouchStart);
            container.removeEventListener("touchmove", drag.onTouchMove);
            container.removeEventListener("touchend", drag.onTouchEnd);
            container.removeEventListener("touchcancel", drag.onTouchEnd);
        }

        if (drag.inertiaTween) {
            drag.inertiaTween.kill();
        }

        container.classList.remove("is-dragging");
        container.style.touchAction = "";
        drag = null;
    }

    /* =========================
       DESKTOP
    ========================= */
    function initDesktop() {
        destroyMobile();
        destroyDesktop();

        container.style.touchAction = "";

        const vh = getStableVH();

        applyScale();

        const dist = getMaxScrollDistanceScreen();

        wrapper.style.height = `${BASE_HEIGHT}px`;
        scaleEl.style.height = `${BASE_HEIGHT}px`;

        container.style.height = `${Math.ceil(dist + vh)}px`;
        container.style.minHeight = `${vh}px`;

        st = ScrollTrigger.create({
            trigger: container,
            start: "top top",
            end: () => `+=${getMaxScrollDistanceScreen()}`,
            scrub: 0.3,
            pin: true,
            pinSpacing: false,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onRefresh(self) {
                const d = getMaxScrollDistanceScreen();
                applyParallaxByScreenX(self.progress * d);
            },
            onUpdate(self) {
                const d = getMaxScrollDistanceScreen();
                applyParallaxByScreenX(self.progress * d);
            }
        });

        applyParallaxByScreenX(0);

        const step = 750;

        function goBy(px) {
            if (!st) return;

            const distNow = getMaxScrollDistanceScreen();
            const currentX = st.progress * distNow;
            const targetX = Math.max(0, Math.min(distNow, currentX + px));
            const progress = distNow === 0 ? 0 : targetX / distNow;
            const targetScroll = st.start + (st.end - st.start) * progress;

            gsap.to(window, {
                scrollTo: targetScroll,
                duration: 1,
                ease: "power2.out",
                overwrite: "auto"
            });
        }

        if (prevBtn) {
            prevBtn.onclick = (e) => {
                e.preventDefault();
                goBy(-step);
            };
        }

        if (nextBtn) {
            nextBtn.onclick = (e) => {
                e.preventDefault();
                goBy(step);
            };
        }

        requestAnimationFrame(() => {
            ScrollTrigger.refresh(true);
        });
    }

    /* =========================
       TOUCH DEVICES
    ========================= */
    function initMobile() {
        destroyDesktop();
        destroyMobile();

        applyScale();

        const vh = getStableVH();
        const s = getScale();
        const unscaledHeight = Math.ceil(vh / Math.max(s, 0.001));

        wrapper.style.height = `${unscaledHeight}px`;
        scaleEl.style.height = `${BASE_HEIGHT}px`;
        container.style.height = `${vh}px`;
        container.style.minHeight = `${vh}px`;
        container.style.touchAction = "none";

        let x = 0;

        applyParallaxByScreenX(0);

        drag = {
            isDown: false,
            pointerId: null,
            startX: 0,
            startPos: 0,
            lastX: 0,
            lastT: 0,
            v: 0,
            inertiaTween: null,
            usePointer: !!window.PointerEvent,
            useTouch: !window.PointerEvent
        };

        function stopInertia() {
            if (drag.inertiaTween) {
                drag.inertiaTween.kill();
                drag.inertiaTween = null;
            }
        }

        function setX(target, animate) {
            const dist = getMaxScrollDistanceScreen();
            const clamped = Math.max(0, Math.min(dist, target));

            if (!animate) {
                x = clamped;
                applyParallaxByScreenX(x);
                return;
            }

            stopInertia();

            drag.inertiaTween = gsap.to(
                { value: x },
                {
                    value: clamped,
                    duration: 1,
                    ease: "power2.out",
                    onUpdate() {
                        x = this.targets()[0].value;
                        applyParallaxByScreenX(x);
                    }
                }
            );
        }

        drag.setX = setX;
        drag.getX = () => x;

        function startGesture(clientX) {
            drag.isDown = true;
            drag.startX = clientX;
            drag.startPos = x;
            drag.lastX = clientX;
            drag.lastT = performance.now();
            drag.v = 0;

            stopInertia();
            container.classList.add("is-dragging");
        }

        function moveGesture(clientX) {
            if (!drag.isDown) return;

            const now = performance.now();
            const dx = clientX - drag.startX;
            const dist = getMaxScrollDistanceScreen();
            const next = Math.max(0, Math.min(dist, drag.startPos - dx));

            const dt = Math.max(16, now - drag.lastT);
            const dd = clientX - drag.lastX;
            drag.v = dd / dt;

            drag.lastX = clientX;
            drag.lastT = now;

            x = next;
            applyParallaxByScreenX(x);
        }

        function endGesture() {
            if (!drag.isDown) return;

            drag.isDown = false;
            container.classList.remove("is-dragging");

            const momentum = isLandscapeLowHeight() ? 0 : (-drag.v * 650);
            setX(x + momentum, true);
        }

        drag.onDown = (e) => {
            if (e.pointerType === "mouse") return;

            drag.pointerId = e.pointerId;
            startGesture(e.clientX);

            try {
                container.setPointerCapture(e.pointerId);
            } catch (_) {}

            e.preventDefault();
        };

        drag.onMove = (e) => {
            if (!drag.isDown) return;
            if (drag.pointerId !== null && e.pointerId !== drag.pointerId) return;

            moveGesture(e.clientX);
            e.preventDefault();
        };

        drag.onUp = (e) => {
            if (!drag.isDown) return;
            if (drag.pointerId !== null && e.pointerId !== drag.pointerId) return;

            drag.pointerId = null;
            endGesture();
            e.preventDefault();
        };

        drag.onTouchStart = (e) => {
            if (!e.touches || !e.touches[0]) return;
            startGesture(e.touches[0].clientX);
        };

        drag.onTouchMove = (e) => {
            if (!drag.isDown || !e.touches || !e.touches[0]) return;
            moveGesture(e.touches[0].clientX);
            e.preventDefault();
        };

        drag.onTouchEnd = () => endGesture();

        if (drag.usePointer) {
            container.addEventListener("pointerdown", drag.onDown, { passive: false });
            container.addEventListener("pointermove", drag.onMove, { passive: false });
            container.addEventListener("pointerup", drag.onUp, { passive: false });
            container.addEventListener("pointercancel", drag.onUp, { passive: false });
        } else {
            container.addEventListener("touchstart", drag.onTouchStart, { passive: true });
            container.addEventListener("touchmove", drag.onTouchMove, { passive: false });
            container.addEventListener("touchend", drag.onTouchEnd, { passive: true });
            container.addEventListener("touchcancel", drag.onTouchEnd, { passive: true });
        }

        const step = 350;

        if (prevBtn) {
            prevBtn.onclick = (e) => {
                e.preventDefault();
                setX(x - step, true);
            };
        }

        if (nextBtn) {
            nextBtn.onclick = (e) => {
                e.preventDefault();
                setX(x + step, true);
            };
        }
    }

    /* =========================
       HOTSPOTS + KEYBOARD
    ========================= */
    function scrollToSpotButton(btn, duration = 0.45) {
        const dist = getMaxScrollDistanceScreen();
        if (dist <= 0) return;

        const s = getScale();
        const spotBaseX = btn.offsetLeft;
        const spotScreenX = spotBaseX * s;
        const target = Math.max(0, Math.min(dist, spotScreenX - SPOT_OFFSET));

        if (isTouchDevice() && drag) {
            drag.setX(target, duration > 0);
            return;
        }

        if (!isTouchDevice() && st) {
            const targetProgress = dist === 0 ? 0 : target / dist;
            const targetScroll = st.start + (st.end - st.start) * targetProgress;

            gsap.to(window, {
                scrollTo: targetScroll,
                duration,
                ease: "power2.out",
                overwrite: "auto"
            });
        }
    }

    function focusSceneElement(target, shouldScrollScene = true) {
        if (!target || typeof target.focus !== "function") return;

        target.focus({ preventScroll: true });

        if (
            shouldScrollScene &&
            target.classList &&
            target.classList.contains("spot")
        ) {
            scrollToSpotButton(target, 0.45);
        }
    }

    function bindSceneKeyboardOrder() {
        const spots = getAllSpots();
        const sceneFocusable = [prevBtn, nextBtn, ...spots].filter(Boolean);

        if (!sceneFocusable.length) return;

        sceneFocusable.forEach((el, index) => {
            el.addEventListener("keydown", (e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    el.click();
                    return;
                }

                if (e.key !== "Tab") return;

                const isLast = index === sceneFocusable.length - 1;
                const isFirst = index === 0;

                // Shift+Tab з першого -> на останній
                if (e.shiftKey && isFirst) {
                    e.preventDefault();
                    const lastEl = sceneFocusable[sceneFocusable.length - 1];
                    focusSceneElement(lastEl, true);
                    return;
                }

                // Tab з останнього -> на початок сцени і перший елемент сцени
                if (!e.shiftKey && isLast) {
                    e.preventDefault();

                    scrollSceneToStart(0.6);

                    const firstEl = sceneFocusable[0];
                    setTimeout(() => {
                        focusSceneElement(firstEl, false);
                        setProgressUI(0);
                    }, 650);

                    return;
                }

                // звичайний цикл всередині сцени
                e.preventDefault();
                const nextIndex = e.shiftKey ? index - 1 : index + 1;
                const nextEl = sceneFocusable[nextIndex];
                focusSceneElement(nextEl, true);
            });

            el.addEventListener("focus", () => {
                // тільки keyboard-focus має рухати сцену
                if (!lastInteractionWasKeyboard) return;

                if (el.classList.contains("spot")) {
                    scrollToSpotButton(el, 0.45);
                }
            });
        });
    }

    document.querySelectorAll(".spot").forEach((spot) => {
        spot.addEventListener("click", (e) => {
            e.preventDefault();

            // щоб mouse click не запускав keyboard-focus scroll двічі
            lastInteractionWasKeyboard = false;

            // одразу відкриваємо модалку
            openSpotModal(spot.getAttribute("data-content") || "");

            requestAnimationFrame(() => {
                fitSketchBorder();
                ensureBorderObserver();
            });

            // і одразу ж рухаємо сцену
            scrollToSpotButton(spot, 0.45);
        });
    });

    bindSceneKeyboardOrder();

    /* =========================
       INIT
    ========================= */
    function initScene() {
        updateRootTouchClass();

        if (isTouchDevice()) {
            initMobile();
        } else {
            initDesktop();
        }
    }

    function queuedInit() {
        if (initRAF) cancelAnimationFrame(initRAF);

        initRAF = requestAnimationFrame(() => {
            initScene();

            requestAnimationFrame(() => {
                if (!isTouchDevice()) {
                    ScrollTrigger.refresh(true);
                    setTimeout(() => ScrollTrigger.refresh(true), 80);
                }
            });
        });
    }

    function onResize() {
        if (resizeRAF) cancelAnimationFrame(resizeRAF);

        resizeRAF = requestAnimationFrame(() => {
            destroyMobile();
            destroyDesktop();
            queuedInit();
        });
    }

    if (document.readyState === "complete") {
        queuedInit();
    } else {
        window.addEventListener("load", queuedInit, { once: true });
    }

    window.addEventListener("pageshow", queuedInit);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", onResize);
    }

    window.addEventListener("beforeunload", () => {
        destroyMobile();
        destroyDesktop();

        if (borderObserver) {
            borderObserver.disconnect();
        }
    });
}